import React, { useState, useEffect } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Stack, Box, TextField, Typography, Switch } from '@mui/material';
import { Upload } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as generateUUID } from 'uuid';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../utils/axiosInstance';
import SingleSelector from '../single-selector/singleSelector';
import fetchDeviceList from '../../utils/fetchDeviceList';
import fetchStreamList from '../../utils/fetchStreamList';
import { appSliceActions } from '../../appSlices/appSlice';

const { Dragger } = Upload;
const VIDEO_UPLOAD_TIMEOUT = 999999999;

function hasWhiteSpace(s) {
  return /\s/g.test(s);
}

const FileUpload = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { adaptorType } = window;
  const hostList = useSelector((state) => state.app.hostList);
  const dispatch = useDispatch();
  const [vstHost, setVstHost] = useState(null);
  const [enableTranscode, setEnableTranscode] = useState(false);
  const [frameRate, setFrameRate] = useState('');
  const [bitrate, setBitrate] = useState('');
  const [tags, setTags] = useState('');

  useEffect(() => {
    if (hostList && hostList.length === 1) {
      setVstHost(hostList[0]);
    }
  }, [hostList]);

  const handleRefresh = async () => {
    hostList.forEach(async (host) => {
      const cameraList = await fetchDeviceList(host);
      if (cameraList != null) {
        for (let i = 0; i < cameraList.length; i += 1) {
          cameraList[i].host = host;
        }
        dispatch(appSliceActions.addDevice(cameraList));
      }
      const streamList = await fetchStreamList(host);
      if (streamList != null) {
        for (let i = 0; i < streamList.length; i += 1) {
          streamList[i].host = host;
        }
        dispatch(appSliceActions.addStream(streamList));
      }
    });
  };

  const handleEnableTranscode = () => {
    setEnableTranscode(!enableTranscode);
  };

  const setFileTag = (fileTags, deviceId) => {
    const jsonData = {
      tags: fileTags,
    };
    axiosInstance
      .post(`${window.location.protocol}//${vstHost}/api/v1/sensor/${deviceId}/info`, jsonData)
      .then((res) => {
        console.log('set tag response: ', res.data);
      })
      .catch((e) => {
        console.debug('failed to set tags', e);
      });
  };

  const customFileUpload = (options) => {
    if (!vstHost) {
      enqueueSnackbar(`Error - ${adaptorType} device not selected`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
      });
      return;
    }
    const { onSuccess, onError, file, onProgress } = options;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('filename', file.name);
    console.log('transcode ?', enableTranscode);
    const uploadHeaders = {
      'content-type': 'multipart/form-data',
      'nvstreamer-chunk-number': 1,
      'nvstreamer-total-chunks': 1,
      'nvstreamer-is-last-chunk': true,
      'nvstreamer-identifier': generateUUID(),
      'nvstreamer-file-name': file.name,
    };
    if (enableTranscode) {
      console.log('adding transcode header');
      uploadHeaders['nvstreamer-enable-transcode'] = true;
    }
    if (enableTranscode && frameRate != null && frameRate !== '') {
      console.log('adding framerate header');
      uploadHeaders['nvstreamer-transcode-framerate'] = frameRate;
    }
    if (enableTranscode && bitrate != null && bitrate !== '') {
      console.log('adding bitrate header');
      uploadHeaders['nvstreamer-transcode-bitrate'] = bitrate;
    }
    console.log('using upload headers', uploadHeaders);
    axiosInstance
      .post(`${window.location.protocol}//${vstHost}/api/v1/storage/file`, fd, {
        timeout: VIDEO_UPLOAD_TIMEOUT,
        headers: uploadHeaders,
        onUploadProgress: (event) => {
          onProgress({ percent: (event.loaded / event.total) * 100 });
        },
      })
      .then((response) => {
        console.log('file upload response', response.data);
        onSuccess('Ok');
        const currentFileTag = tags;
        const currentFileDeviceId = response.data.id;
        setFileTag(currentFileTag, currentFileDeviceId);
        handleRefresh();
      })
      .catch((error) => {
        console.debug('File upload error', error);
        onError({ error });
        enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
      });
  };

  const props = {
    multiple: true,
    customRequest: customFileUpload,
    onRemove: (file) => {
      console.log('Remove clicked', file);
    },
    // eslint-disable-next-line consistent-return
    beforeUpload: (file) => {
      if (hasWhiteSpace(file.name)) {
        enqueueSnackbar(`Error - whitespaces not allowed in file name`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        file.prakhar = 'hi';
        return false;
      }
      if (!vstHost) {
        enqueueSnackbar(`Error - please select ${adaptorType} device`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        return false;
      }
      return true;
    },
  };

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <SingleSelector
          id="remove-vst"
          placeholder="Type to search"
          label={`Select ${adaptorType} device*`}
          value={vstHost}
          onChange={setVstHost}
          getOptionLabel={(option) => `${adaptorType} at ${option}`}
          options={hostList}
        />
        <TextField
          fullWidth
          label="Tags"
          value={tags}
          onChange={(e) => {
            setTags(e.target.value);
          }}
          placeholder="tag1, tag2... tagn"
        />
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle2">Transcode:</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1 }}>
            <Switch checked={enableTranscode} onChange={handleEnableTranscode} />
          </Stack>
        </Stack>
        <TextField
          fullWidth
          label="Transcode framerate"
          value={frameRate}
          onChange={(e) => {
            setFrameRate(e.target.value);
          }}
          placeholder=""
          disabled={!enableTranscode}
        />
        <TextField
          fullWidth
          label="Transcode bitrate"
          value={bitrate}
          onChange={(e) => {
            setBitrate(e.target.value);
          }}
          placeholder=""
          disabled={!enableTranscode}
        />
      </Stack>
      <Dragger {...props} disabled={!vstHost}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          {vstHost
            ? `Click or drag file to this area to upload`
            : `Please select ${adaptorType} device before uploading files`}
        </p>
        <p className="ant-upload-hint">Single or bulk upload supported. Provide tags before uploading files. </p>
      </Dragger>
    </Box>
  );
};
export default FileUpload;
